import React from 'react';
import './Privacy.css';

const Privacy: React.FC = () => {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <div className="privacy-header">
          <h1 className="privacy-title">Privacy Policy</h1>
          <p className="privacy-subtitle">Last updated: January 2025</p>
        </div>

        <div className="privacy-content">
          <section className="privacy-section">
            <h2 className="section-title">Introduction</h2>
            <p className="section-text">
              GetStrandX ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use our 
              peer-to-peer roadside assistance platform.
            </p>
          </section>

          <section className="privacy-section">
            <h2 className="section-title">Information We Collect</h2>
            <h3 className="subsection-title">Personal Information</h3>
            <ul className="privacy-list">
              <li>Name, email address, and phone number</li>
              <li>Government-issued ID for identity verification and background checks</li>
              <li>Vehicle information (make, model, year, license plate, VIN)</li>
              <li>Payment method information (cards, PayPal, Venmo, Zelle)</li>
              <li>Profile photos and verification documents</li>
              <li>Social media authentication data (when using social login)</li>
            </ul>

            <h3 className="subsection-title">Location Information</h3>
            <ul className="privacy-list">
              <li>Real-time GPS location for service matching</li>
              <li>Location history for service requests</li>
              <li>Address information for service delivery</li>
            </ul>

            <h3 className="subsection-title">Usage Information</h3>
            <ul className="privacy-list">
              <li>Service requests, completion data, and service history</li>
              <li>Bidirectional ratings and reviews (requester ↔ helper)</li>
              <li>In-app messaging and communication logs</li>
              <li>Equipment and service capabilities (for helpers)</li>
              <li>Payment transactions and payout history</li>
              <li>Device information, FCM tokens, and app usage analytics</li>
              <li>Service-specific answers and custom form responses</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2 className="section-title">How We Use Your Information</h2>
            <ul className="privacy-list">
              <li>Provide and improve our peer-to-peer roadside assistance services</li>
              <li>Match requesters with nearby helpers based on location, skills, and ratings</li>
              <li>Verify user identity through government ID and optional background checks</li>
              <li>Process payments, calculate platform fees, and facilitate helper payouts</li>
              <li>Send real-time notifications for service requests and updates</li>
              <li>Track service completion and maintain quality through ratings</li>
              <li>Enable equipment tracking and service capability matching</li>
              <li>Maintain platform security, prevent fraud, and handle disputes</li>
              <li>Analyze usage patterns to improve matching algorithms and services</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2 className="section-title">Information Sharing</h2>
            <p className="section-text">
              We do not sell your personal information. We may share your information only in the following circumstances:
            </p>
            <ul className="privacy-list">
              <li>With matched helpers/requesters during active service requests (location, contact info)</li>
              <li>With payment processors (Stripe, PayPal) for transaction processing</li>
              <li>With identity verification providers for background checks</li>
              <li>With equipment and service providers for capability matching</li>
              <li>When required by law, court order, or to protect our rights</li>
              <li>In case of emergency, safety concerns, or dispute resolution</li>
              <li>With analytics providers for service improvement (anonymized data only)</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2 className="section-title">Data Security</h2>
            <p className="section-text">
              We implement industry-standard security measures to protect your information:
            </p>
            <ul className="privacy-list">
              <li>End-to-end encryption for sensitive communications</li>
              <li>Secure data storage and transmission</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and authentication protocols</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2 className="section-title">Your Rights</h2>
            <p className="section-text">You have the right to:</p>
            <ul className="privacy-list">
              <li>Access and review your personal information</li>
              <li>Correct inaccurate information</li>
              <li>Delete your account and associated data</li>
              <li>Opt out of certain communications</li>
              <li>Request data portability</li>
            </ul>
          </section>

        </div>
      </div>
    </div>
  );
};

export default Privacy;
